const parseFilenameFromContentDisposition = (contentDisposition) => {
  if (!contentDisposition) {
    return null;
  }

  const filenameMatch = contentDisposition.match(/filename\s*=\s*"([^"]+)"/);
  return filenameMatch?.[1] || null;
};

export const fetchUserRegistrations = async ({
  registrationBaseUrl,
  email,
  conferenceName,
}) => {
  const url = `${registrationBaseUrl}/registration/user/${encodeURIComponent(
    email
  )}/${encodeURIComponent(conferenceName)}`;
  const response = await fetch(url);

  if (!response.ok) {
    throw new Error("Unable to verify existing registrations. Please retry before saving or paying.");
  }

  const registrations = await response.json();
  if (!Array.isArray(registrations)) {
    throw new Error("Invalid registration lookup response");
  }
  return registrations;
};

export const fetchPapersByRegistrationId = async ({
  registrationBaseUrl,
  registrationId,
}) => {
  const url = `${registrationBaseUrl}/api/papers/registration/${registrationId}`;
  const response = await fetch(url);

  console.log("🔍 Fetching papers for registration ID:", registrationId);
  console.log("🔍 Paper API URL:", url);
  console.log("🔍 Paper API response status:", response.status);

  if (!response.ok) {
    return [];
  }

  const papers = await response.json();
  console.log("🔍 Papers array from API:", papers);
  console.log("🔍 Number of papers:", papers?.length);

  return Array.isArray(papers) ? papers : [];
};

export const fetchPaperFileMetadata = async ({
  registrationBaseUrl,
  registrationId,
}) => {
  const url = `${registrationBaseUrl}/registration/${registrationId}/file/paper`;
  console.log("🔍 Checking for file at:", url);

  const response = await fetch(url, { method: "HEAD" });
  console.log("🔍 File check response status:", response.status);

  if (!response.ok) {
    console.log("📎 No paper file found, status:", response.status);
    return {
      exists: false,
      fileName: "uploaded-paper.pdf",
      status: response.status,
    };
  }

  const contentDisposition = response.headers.get("Content-Disposition");
  console.log("🔍 Content-Disposition header:", contentDisposition);

  const fileName =
    parseFilenameFromContentDisposition(contentDisposition) || "uploaded-paper.pdf";

  if (fileName === "uploaded-paper.pdf" && contentDisposition) {
    console.log("📎 Could not extract filename from header");
  } else if (fileName !== "uploaded-paper.pdf") {
    console.log("📎 Extracted filename:", fileName);
  }

  console.log("📎 Paper file exists. Final filename:", fileName);

  return {
    exists: true,
    fileName,
    status: response.status,
  };
};

export const fetchCoAuthorsByPaperId = async ({
  registrationBaseUrl,
  paperId,
}) => {
  const response = await fetch(
    `${registrationBaseUrl}/api/coauthors/paper/${paperId}`
  );

  if (!response.ok) {
    return [];
  }

  const coAuthors = await response.json();
  return Array.isArray(coAuthors) ? coAuthors : [];
};

export const fetchPaperCount = async ({
  registrationBaseUrl,
  email,
  conference,
}) => {
  const url = `${registrationBaseUrl}/api/papers/count?email=${encodeURIComponent(
    email
  )}&conference=${encodeURIComponent(conference)}`;
  const response = await fetch(url);

  if (!response.ok) {
    return 0;
  }

  const count = await response.json();
  const parsedCount = Number(count);
  return Number.isFinite(parsedCount) ? parsedCount : 0;
};

export const fetchRegistrationFeeForSelection = async ({
  conferenceApi,
  conferenceId,
  categoryId,
  category,
  registrationTypeId,
  registrationType,
}) => {
  const today = new Date().toISOString().split("T")[0];
  const url = `${conferenceApi}/fees/conference/${conferenceId}/valid-on?date=${today}`;
  const response = await fetch(url);

  if (!response.ok) {
    if (response.status === 404) {
      throw new Error("No fee configuration found for the current date");
    }
    throw new Error("Failed to fetch registration fee");
  }

  const validFees = await response.json();
  const fees = Array.isArray(validFees) ? validFees : [];

  const normalizeId = (value) =>
    value === null || value === undefined || value === "" ? null : String(value);
  const normalizeText = (value) =>
    value === null || value === undefined
      ? ""
      : String(value).trim().toLowerCase();
  const detailId = (detail, fallback) =>
    normalizeId(detail?.id ?? detail?.detailId ?? fallback);
  const detailValue = (detail, fallback) =>
    normalizeText(detail?.value ?? detail?.name ?? fallback);

  const selectedCategoryId = normalizeId(categoryId);
  const selectedRegistrationTypeId = normalizeId(registrationTypeId);
  const selectedCategoryValue = normalizeText(category);
  const selectedRegistrationTypeValue = normalizeText(registrationType);

  const matchesCategory = (fee) => {
    const feeCategoryId = detailId(
      fee.participantType,
      fee.participantTypeId ?? fee.categoryId
    );
    const feeCategoryValue = detailValue(
      fee.participantType,
      fee.participantTypeValue ?? fee.category
    );

    return (
      (selectedCategoryId && feeCategoryId === selectedCategoryId) ||
      (selectedCategoryValue && feeCategoryValue === selectedCategoryValue)
    );
  };

  const matchesRegistrationType = (fee) => {
    if (!selectedRegistrationTypeId && !selectedRegistrationTypeValue) {
      return false;
    }

    const feeTypeId = detailId(
      fee.eventType,
      fee.eventTypeId ?? fee.registrationTypeId
    );
    const feeTypeValue = detailValue(
      fee.eventType,
      fee.eventTypeValue ?? fee.registrationType
    );

    return (
      (selectedRegistrationTypeId && feeTypeId === selectedRegistrationTypeId) ||
      (selectedRegistrationTypeValue &&
        feeTypeValue === selectedRegistrationTypeValue)
    );
  };

  const isCategoryOnlyFee = (fee) =>
    !fee.eventType &&
    !fee.eventTypeId &&
    !fee.registrationTypeId &&
    !fee.eventTypeValue &&
    !fee.registrationType;

  const activeCategoryFees = fees.filter(
    (fee) => matchesCategory(fee) && fee.isActive === true
  );
  const typeSpecificFee =
    selectedRegistrationTypeId || selectedRegistrationTypeValue
      ? activeCategoryFees.find(matchesRegistrationType)
      : null;
  const categoryOnlyFee = activeCategoryFees.find(isCategoryOnlyFee);
  const matchingFee = typeSpecificFee || categoryOnlyFee || null;

  if (!matchingFee) {
    console.warn("No matching registration fee found", {
      conferenceId,
      selectedCategoryId,
      selectedCategoryValue,
      selectedRegistrationTypeId,
      selectedRegistrationTypeValue,
      validFeeCount: fees.length,
      activeCategoryFeeCount: activeCategoryFees.length,
    });
  }

  return matchingFee;
};

export const fetchAccompanyingPersonFee = async ({
  conferenceApi,
  conferenceId,
}) => {
  const url = `${conferenceApi}/fees/conference/${conferenceId}/AccompanyingFee`;
  const response = await fetch(url);

  if (!response.ok) {
    console.error("❌ Failed to fetch accompanying person fee");
    console.error("Status:", response.status);
    console.error("Status Text:", response.statusText);
    return null;
  }

  const feeData = await response.json();

  if (Array.isArray(feeData)) {
    return feeData.find((fee) => fee.isActive === true) || null;
  }

  if (feeData && typeof feeData === "object") {
    return feeData.isActive === true || feeData.isActive === undefined
      ? feeData
      : null;
  }

  return null;
};

export const fetchConveyanceFeePercentage = async ({
  conferenceApi,
  conferenceId,
}) => {
  const url = `${conferenceApi}/${conferenceId}/master/type/conveyanceFee/details`;
  const response = await fetch(url);

  if (!response.ok) {
    return null;
  }

  const conveyanceFeeData = await response.json();
  let value = null;

  if (Array.isArray(conveyanceFeeData)) {
    if (conveyanceFeeData.length === 0) {
      return null;
    }
    const activeConveyanceFee =
      conveyanceFeeData.find((fee) => fee.isActive === true) ||
      conveyanceFeeData[0];
    if (activeConveyanceFee) {
      value =
        activeConveyanceFee.value ||
        activeConveyanceFee.percentage ||
        activeConveyanceFee.rate;
    }
  } else if (conveyanceFeeData && typeof conveyanceFeeData === "object") {
    value =
      conveyanceFeeData.value ||
      conveyanceFeeData.percentage ||
      conveyanceFeeData.rate;
  }

  if (value !== null && value !== undefined && value !== "") {
    const parsedValue = parseFloat(value);
    return !Number.isNaN(parsedValue) && parsedValue > 0 ? parsedValue : null;
  }

  return null;
};

export const fetchFocusedSessions = async ({ conferenceApi, conferenceId }) => {
  const url = `${conferenceApi}/${conferenceId}/master/type/FocussedSessions/details`;
  const response = await fetch(url);

  if (!response.ok) {
    console.error("❌ Failed to fetch focused sessions");
    console.error("Status:", response.status);
    console.error("Status Text:", response.statusText);
    return [];
  }

  const sessionsData = await response.json();
  return Array.isArray(sessionsData) ? sessionsData : [];
};

export const fetchGenderOptions = async ({ conferenceApi, conferenceId }) => {
  const url = `${conferenceApi}/${conferenceId}/master/type/gender/details`;
  console.log("🔍 Fetching gender options from:", url);

  const response = await fetch(url);

  if (!response.ok) {
    console.error("❌ Failed to fetch gender options");
    console.error("Status:", response.status);
    return [];
  }

  const genderData = await response.json();
  console.log("📦 Gender options received:", genderData);

  return Array.isArray(genderData) ? genderData : [];
};

export const fetchFocusedSessionFee = async ({
  conferenceApi,
  conferenceId,
  sessionId,
}) => {
  const url = `${conferenceApi}/fees/conference/${conferenceId}/fee/${sessionId}`;
  console.log(`🔍 Fetching focused session fee for session ${sessionId} from:`, url);

  const response = await fetch(url);

  if (!response.ok) {
    console.error(`❌ Failed to fetch fee for focused session ${sessionId}`);
    console.error("Status:", response.status);
    console.error("Status Text:", response.statusText);
    return null;
  }

  const feeData = await response.json();
  console.log(
    `📦 Raw API response for session ${sessionId}:`,
    JSON.stringify(feeData, null, 2)
  );
  console.log(`💰 Fee rate extracted:`, feeData?.rate);
  console.log(`💵 Fee currency extracted:`, feeData?.currency);

  return feeData;
};

export const fetchFocusedSessionAvailability = async ({
  conferenceApi,
  sessionId,
}) => {
  const url = `${conferenceApi}/seats/${sessionId}/availability`;
  console.log(
    `🔍 Fetching focused session availability for session ${sessionId} from:`,
    url
  );

  const response = await fetch(url);

  if (!response.ok) {
    console.error(`❌ Failed to fetch availability for focused session ${sessionId}`);
    console.error("Status:", response.status);
    console.error("Status Text:", response.statusText);
    return null;
  }

  const availabilityData = await response.json();
  console.log(
    `📦 Availability response for session ${sessionId}:`,
    JSON.stringify(availabilityData, null, 2)
  );

  return availabilityData;
};

export const fetchAbstractMetadata = async ({ conferenceApi, conferenceId }) => {
  const response = await fetch(
    `${conferenceApi}/api/abstract-metadata/conference/${conferenceId}`
  );
  const data = await response.json();
  return Array.isArray(data) ? data[0] : data;
};

export const fetchTracks = async ({ conferenceApi, conferenceId }) => {
  const url = `${conferenceApi}/api/tracks/conference/${conferenceId}`;
  console.log("📡 Fetching tracks from:", url);
  const response = await fetch(url);
  console.log("📡 Tracks response status:", response.status);
  const data = await response.json();
  return Array.isArray(data) ? data : [];
};

export const fetchConferenceByHost = async ({ conferenceApi, host }) => {
  const response = await fetch(`${conferenceApi}/${host}/json`);
  if (!response.ok) {
    throw new Error(`Failed to fetch conference for host ${host}`);
  }
  return await response.json();
};

export const fetchMasterDetails = async ({
  conferenceApi,
  conferenceId,
  type,
}) => {
  const response = await fetch(
    `${conferenceApi}/${conferenceId}/master/type/${type}/details`
  );
  if (!response.ok) {
    throw new Error(
      `Failed to fetch master details for conference ${conferenceId}, type ${type}`
    );
  }
  const data = await response.json();
  if (Array.isArray(data)) {
    return data;
  }
  if (Array.isArray(data?.data)) {
    return data.data;
  }
  if (Array.isArray(data?.details)) {
    return data.details;
  }
  if (Array.isArray(data?.masterDetails)) {
    return data.masterDetails;
  }
  return [];
};

export const fetchCountries = async () => {
  const response = await fetch("https://restcountries.com/v3.1/all?fields=name");
  const data = await response.json();
  return (Array.isArray(data) ? data : [])
    .map((country) => {
      const name =
        typeof country?.name === "string"
          ? country.name
          : country?.name?.common ||
            country?.name?.official ||
            country?.name?.nativeName ||
            null;
      if (!name) return null;
      return { label: name, value: name };
    })
    .filter(Boolean)
    .sort((a, b) => a.label.localeCompare(b.label));
};

export const fetchPaperDiscountRules = async ({ conferenceApi, conferenceId }) => {
  const response = await fetch(
    `${conferenceApi}/${conferenceId}/master/type/paperDiscount/details`
  );
  if (!response.ok) {
    return [];
  }
  const details = await response.json();
  return Array.isArray(details) ? details : [];
};

export const createPaymentOrder = async ({ orderUrl, payload }) => {
  const response = await fetch(orderUrl, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });

  const result = {
    ok: response.ok,
    status: response.status,
    statusText: response.statusText,
    order: null,
    errorText: "",
  };

  if (!response.ok) {
    result.errorText = await response.text();
    return result;
  }

  result.order = await response.json();
  return result;
};

export const savePaymentWithBreakdown = async ({ paymentSaveUrl, payload }) => {
  const response = await fetch(paymentSaveUrl, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });

  const result = {
    ok: response.ok,
    status: response.status,
    statusText: response.statusText,
    data: null,
    errorText: "",
  };

  if (!response.ok) {
    result.errorText = await response.text();
    return result;
  }

  result.data = await response.json();
  return result;
};

export const confirmRegistration = async ({
  registrationBaseUrl,
  registrationId,
}) => {
  const response = await fetch(
    `${registrationBaseUrl}/registration/${registrationId}/confirm`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
    }
  );

  const result = {
    ok: response.ok,
    status: response.status,
    confirmed: null,
    errorText: "",
  };

  if (!response.ok) {
    result.errorText = await response.text();
    return result;
  }

  result.confirmed = await response.json();
  return result;
};

export const saveConferenceRegistration = async ({
  conferenceRegistrationUrl,
  registrationId,
  payload,
  forceCreate = false,
}) => {
  const isUpdate = !forceCreate && !!registrationId;
  const response = await fetch(
    isUpdate ? `${conferenceRegistrationUrl}/${registrationId}` : conferenceRegistrationUrl,
    {
      method: isUpdate ? "PUT" : "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );

  const result = {
    ok: response.ok,
    status: response.status,
    statusText: response.statusText,
    data: null,
    errorText: "",
  };

  if (!response.ok) {
    result.errorText = await response.text();
    return result;
  }

  result.data = await response.json();
  return result;
};

export const uploadRegistrationFile = async ({
  registrationBaseUrl,
  registrationId,
  fileType,
  payload,
}) => {
  const response = await fetch(
    `${registrationBaseUrl}/registration/${registrationId}/file/${fileType}`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );

  const result = {
    ok: response.ok,
    status: response.status,
    statusText: response.statusText,
    data: null,
    errorText: "",
  };

  if (!response.ok) {
    result.errorText = await response.text();
    return result;
  }

  result.data = await response.json();
  return result;
};

export const savePaperSubmission = async ({
  registrationBaseUrl,
  paperId,
  payload,
}) => {
  const isUpdate = !!paperId;
  const response = await fetch(
    isUpdate
      ? `${registrationBaseUrl}/api/papers/${paperId}`
      : `${registrationBaseUrl}/api/papers`,
    {
      method: isUpdate ? "PUT" : "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );

  const result = {
    ok: response.ok,
    status: response.status,
    statusText: response.statusText,
    data: null,
    errorText: "",
  };

  if (!response.ok) {
    result.errorText = await response.text();
    return result;
  }

  result.data = await response.json();
  return result;
};

export const deleteCoAuthorsByPaperId = async ({
  registrationBaseUrl,
  paperId,
}) => {
  const response = await fetch(
    `${registrationBaseUrl}/api/coauthors/paper/${paperId}`,
    { method: "DELETE" }
  );

  return {
    ok: response.ok,
    status: response.status,
    statusText: response.statusText,
  };
};

export const saveCoAuthor = async ({ registrationBaseUrl, payload }) => {
  const response = await fetch(`${registrationBaseUrl}/api/coauthors`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });

  const result = {
    ok: response.ok,
    status: response.status,
    statusText: response.statusText,
    data: null,
    errorText: "",
  };

  if (!response.ok) {
    result.errorText = await response.text();
    return result;
  }

  result.data = await response.json();
  return result;
};

export const fetchRegistrationById = async ({
  conferenceRegistrationUrl,
  registrationId,
}) => {
  const response = await fetch(`${conferenceRegistrationUrl}/${registrationId}`);

  if (!response.ok) {
    return null;
  }

  return await response.json();
};
